import { useState, useEffect } from 'react'
import { BellRing } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import MarketingConsent, { MarketingConsentState } from './MarketingConsent'
import { api } from '../../lib/api'
import { useToastStore } from '../../store/toastStore'
import { useAuthStore } from '../../store/authStore'

interface MarketingConsentModalProps {
  isOpen: boolean
}

export default function MarketingConsentModal({ isOpen }: MarketingConsentModalProps) {
  const { user, dismissMarketingModal, marketingConsentSaved } = useAuthStore()
  const { addToast } = useToastStore()
  const { t } = useTranslation(['auth'])
  const [submitting, setSubmitting] = useState(false)
  const [consents, setConsents] = useState<MarketingConsentState>({
    email_marketing: false,
    phone_marketing: false,
  })

  // Pre-populate from user's current state when modal opens
  useEffect(() => {
    if (isOpen && user?.marketing_consent_state) {
      setConsents({
        email_marketing: user.marketing_consent_state.email_marketing ?? false,
        phone_marketing: user.marketing_consent_state.phone_marketing ?? false,
      })
    }
  }, [isOpen, user?.marketing_consent_state])

  if (!isOpen) return null

  const handleSave = async () => {
    setSubmitting(true)
    try {
      const response = await api.put('/marketing-consents/', {
        state: consents,
        source: 'prompt',
      })
      marketingConsentSaved(response.data.state)
      addToast(t('auth:marketingConsent.modal.savedToast'), 'success')
    } catch (error) {
      console.error('Failed to save marketing consents:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDismiss = async () => {
    try {
      await api.post('/marketing-consents/dismiss')
    } catch {
      // Best-effort — dismiss locally regardless
    }
    dismissMarketingModal()
    addToast(t('auth:marketingConsent.modal.dismissedToast'), 'info')
  }

  const handleBackdropClick = () => {
    handleDismiss()
  }

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto">
      {/* Backdrop — dismissible (unlike the legal modal) */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />

      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg bg-white rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <BellRing className="h-7 w-7 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {t('auth:marketingConsent.modal.title')}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t('auth:marketingConsent.modal.subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <MarketingConsent
              value={consents}
              onChange={setConsents}
              disabled={submitting}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={handleDismiss}
              disabled={submitting}
              className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {t('auth:marketingConsent.modal.laterButton')}
            </button>

            <button
              onClick={handleSave}
              disabled={submitting}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>{t('auth:marketingConsent.modal.saving')}</span>
                </div>
              ) : (
                t('auth:marketingConsent.modal.saveButton')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
